import { PageMeta } from "@/components/PageMeta";
import { PolicyLayout, type PolicySection } from "@/components/PolicyLayout";
import { siteConfig } from "@/config/site";
import { routeSeo } from "@/content/seo";

const sections: PolicySection[] = [
  { id: "current-status", label: "1. Current status" },
  { id: "how-to-cancel", label: "2. How to cancel" },
  { id: "effective-timing", label: "3. When cancellation takes effect" },
  { id: "access-through-period", label: "4. Access through the paid period" },
  { id: "renewal", label: "5. Preventing renewal" },
  { id: "data-after-cancellation", label: "6. Data access after cancellation" },
  { id: "account-deletion", label: "7. Cancellation vs. account deletion" },
  { id: "enterprise", label: "8. Enterprise agreements" },
  { id: "contact", label: "9. Contact" },
];

export function CancellationPage() {
  const seo = routeSeo["/legal/cancellation"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/legal/cancellation" />
      <PolicyLayout
        slug="cancellation"
        title="Cancellation Policy"
        intro="This policy explains how to cancel a paid Devory subscription and what happens when you do."
        sections={sections}
      >
        <h2 id="current-status">1. Current status</h2>
        <p>
          Devory Cloud offers real, live paid subscriptions (Builder, Professional, Studio and
          Team), billed through Stripe. This policy governs cancellation of those subscriptions.
        </p>

        <h2 id="how-to-cancel">2. How to cancel</h2>
        <p>
          Cancel a subscription from Settings &gt; Billing inside Devory Cloud, or by contacting
          Devory directly if you cannot access self-service cancellation for your plan.
        </p>

        <h2 id="effective-timing">3. When cancellation takes effect</h2>
        <p>
          By default, cancellation stops future renewal rather than immediately ending a
          subscription part-way through a period you have already paid for. You may separately
          request immediate cancellation.
        </p>

        <h2 id="access-through-period">4. Access through the paid period</h2>
        <p>
          Customers who cancel retain access to paid features through the end of the period already
          paid for, unless a refund for that period is separately issued under the{" "}
          <a href="/legal/refunds">Refund Policy</a>.
        </p>

        <h2 id="renewal">5. Preventing renewal</h2>
        <p>
          Cancelling before your next renewal date prevents further charges. Devory confirms
          cancellation so you have a clear record that renewal has been stopped.
        </p>

        <h2 id="data-after-cancellation">6. Data access after cancellation</h2>
        <p>
          Cancellation ends paid access but is not automatically the same as deleting your data.
          Devory gives customers a reasonable window to export their projects and data after
          cancellation before any deletion occurs; the specific advance-notice window is not yet
          separately published -- contact Devory if you need clarity on your own account before
          cancelling.
        </p>

        <h2 id="account-deletion">7. Cancellation vs. account deletion</h2>
        <p>
          Cancelling a paid subscription is distinct from deleting your account entirely. Account
          deletion removes your account and associated data subject to Devory's data retention
          practices; cancelling a subscription only stops future billing.
        </p>

        <h2 id="enterprise">8. Enterprise agreements</h2>
        <p>
          Where a signed enterprise agreement includes its own cancellation and offboarding terms,
          those terms take precedence over this public policy for that customer.
        </p>

        <h2 id="contact">9. Contact</h2>
        <p>
          Billing and cancellation questions can be sent to{" "}
          <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a>.
        </p>
      </PolicyLayout>
    </>
  );
}
